import {
  CalculationMethod,
  Coordinates as AdhanCoordinates,
  HighLatitudeRule,
  Madhab,
  PrayerTimes,
} from 'adhan';
import { deviceTimeZone, keyParts, zonedParts, type DateKey, type TimeZoneName } from './date.js';
import {
  placeholderTimesFor,
  type DayTimes,
  type PrayerTimesProvider,
  type TimesSettings,
} from './times.js';

function valid(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

function toHHMM(date: Date, timeZone: TimeZoneName, fallback: string): string {
  if (!valid(date)) return fallback;
  const p = zonedParts(date, timeZone);
  return `${`${p.hour}`.padStart(2, '0')}:${`${p.minute}`.padStart(2, '0')}`;
}

/**
 * Real prayer times, from `adhan`, read on the wall clock of `timeZone`.
 *
 * The choices this exposes are the ones that genuinely change the answer:
 * where you are, which convention your community follows for the twilight
 * angles, and which madhab's rule for the length of the ‘Asr shadow. Everything
 * else the library offers is deliberately not surfaced.
 *
 * `timeZone` is an IANA name and defaults to the device's. A household passes
 * its own, so the same minute shows on every device whatever zone it is in.
 *
 * Falls back to the placeholder when no location has been set, so the app never
 * shows times it has no basis for.
 */
export function createTimesProvider(
  settings: TimesSettings,
  timeZone: TimeZoneName = deviceTimeZone(),
): PrayerTimesProvider {
  if (!settings.coordinates) return placeholderTimesFor(timeZone);

  const { latitude, longitude } = settings.coordinates;
  const coordinates = new AdhanCoordinates(latitude, longitude);

  function compute(date: Date, rule?: (typeof HighLatitudeRule)[keyof typeof HighLatitudeRule]) {
    const params = CalculationMethod[settings.method]();
    params.madhab = settings.madhab === 'hanafi' ? Madhab.Hanafi : Madhab.Shafi;
    if (rule) params.highLatitudeRule = rule;
    return new PrayerTimes(coordinates, date, params);
  }

  return {
    id: `adhan:${settings.method}:${settings.madhab}`,
    timeZone,
    forDate(dateKey: DateKey): DayTimes {
      // `adhan` reads the calendar day from the date's own (process-local)
      // fields, so the date is built from the key's fields — at noon, where no
      // clock change can move it onto another day — whatever zone is wanted.
      const { year, month, day } = keyParts(dateKey);
      const date = new Date(year, month - 1, day, 12);

      /*
       * `adhan` returns an Invalid Date for a prayer whose defining moment does
       * not happen — a twilight angle the sun never reaches, which is every
       * summer night far enough north. Formatting one gave 'NaN:NaN', and the
       * first screen to put that through Intl threw and took the app down.
       *
       * The library's own high latitude rule answers almost all of these, but
       * it REPLACES the angle the chosen method is built on, so applying it
       * everywhere would quietly make the calculation method stop mattering as
       * far south as London. It is brought in only when the plain calculation
       * has not produced a usable day.
       */
      let times = compute(date);
      if (![times.fajr, times.dhuhr, times.asr, times.maghrib, times.isha].every(valid)) {
        times = compute(date, HighLatitudeRule.recommended(coordinates));
      }

      // And where even that cannot say — a polar night has no sunset to find —
      // the sample time stands in for that prayer rather than nothing at all.
      const sample = placeholderTimesFor(timeZone).forDate(dateKey);
      return {
        fajr: toHHMM(times.fajr, timeZone, sample.fajr),
        sunrise: toHHMM(times.sunrise, timeZone, sample.sunrise),
        dhuhr: toHHMM(times.dhuhr, timeZone, sample.dhuhr),
        asr: toHHMM(times.asr, timeZone, sample.asr),
        maghrib: toHHMM(times.maghrib, timeZone, sample.maghrib),
        isha: toHHMM(times.isha, timeZone, sample.isha),
      };
    },
  };
}
